import type { VideoModelConfig } from '../../types';

/** MiniMax H3（多模态独占：首尾帧、音视频参考、文生/图生视频） */
export const minimaxH3: VideoModelConfig = {
  id: '01KZ2PZ01A08NF3RR0PEXM624C',
  displayName: 'MiniMax H3',
  defaults: {
    ratio: '9:16',
    resolution: '768P',
    duration: 5,
  },
  ratioOptions: [
    { label: '1:1', value: '1:1' },
    { label: '3:4', value: '3:4' },
    { label: '4:3', value: '4:3' },
    { label: '9:16', value: '9:16' },
    { label: '16:9', value: '16:9' },
    { label: '21:9', value: '21:9' },
    { label: '自适应', value: 'adaptive' },
  ],
  resolutionOptions: [
    { label: '768P', value: '768P' },
    { label: '2K', value: '2K' },
  ],
  duration: { mode: 'range', min: 4, max: 15, step: 1, default: 5 },
  capabilities: {
    refVideo: true,
    refAudio: true,
    multiModalReference: true,
    forceAdaptiveKeyframe: true,
    tailOnlyKeyframe: true,
    multiVideo: true,
  },
  limits: {
    maxRefImages: 9,
    maxRefVideos: 3,
    maxRefAudios: 3,
    // 参考视频上传约束（转写自 VideoSelectModal 原硬编码）：格式 mp4/mov、单文件 ≤50MB、时长 [2,15]s
    refVideoConstraints: {
      formats: ['mp4', 'mov'],
      maxFileSizeMB: 50,
      duration: { min: 2, max: 15 },
      hintKey: 'imageCreation.videoUploadHint215s',
    },
    // 参考音频上传约束（转写自 AudioSelectModal 原硬编码）：格式 wav/mp3、单文件 ≤15MB、时长 [2,15]s
    refAudioConstraints: {
      formats: ['wav', 'mp3'],
      maxFileSizeMB: 15,
      duration: { min: 2, max: 15 },
      hintKey: 'imageCreation.audioUploadHint',
    },
    // 提交前参考图约束（汇总层校验）：jpg/jpeg/png、尺寸 256~5760、宽高比 [0.4,2.5]、单张 ≤30MB
    // ⚠️ 与 refImageConstraints（上传时单文件层）是「两层独立校验」；尺寸/宽高比/大小仅在提交前汇总时校验
    refImageSubmitConstraints: {
      formats: ['jpg', 'jpeg', 'png'],
      dimension: { min: 256, max: 5760 },
      aspectRatio: { min: 0.4, max: 2.5 },
      maxFileSizeMB: 30,
      // 图片宽/高/大小用 MiniMax H3 专属文案（行为等价）；格式/宽高比回落通用文案
      errorKeys: {
        width: 'imageCreation.minimaxH3ImageWidthOutOfRange',
        height: 'imageCreation.minimaxH3ImageHeightOutOfRange',
        size: 'imageCreation.minimaxH3ImageSingleSizeExceeded',
      },
    },
    // 提交前参考视频约束（汇总层校验）：mp4/mov、尺寸 256~5760、宽高比 [0.4,2.5]、单段 2~15s、帧率 23.976~60、单文件 ≤50MB、合计 ≤15s
    // ⚠️ 与 refVideoConstraints（上传时单文件层）是「两层独立校验」；帧率/尺寸/宽高比/合计时长仅在提交前汇总时校验
    refVideoSubmitConstraints: {
      formats: ['mp4', 'mov'],
      maxFileSizeMB: 50,
      dimension: { min: 256, max: 5760 },
      aspectRatio: { min: 0.4, max: 2.5 },
      fps: { min: 23.976, max: 60 },
      duration: { min: 2, max: 15, totalMax: 15 },
    },
  },
  /** 画布扩展（节点式业务专属字段，从画布 configs/minimax-h3.ts 迁移） */
  canvas: {
    ratioLockByTab: {
      '首尾帧': 'adaptive',
    },
    capabilities: {
      refVideo: true,
      refAudio: true,
      atMentionTabs: ['全能参考', '图生视频', '视频编辑', '视频延长'],
    },
    limits: {
      validateRefImageSize: true,
      promptMaxLength: 7000,
      maxRefImages: 9,
      maxRefVideos: 3,
      maxRefAudios: 3,
      refImageConstraints: {
        formats: ['jpg', 'jpeg', 'png', 'webp', 'bmp'],
        maxFileSizeMB: 30,
        dimension: { min: 256, max: 5760 },
        aspectRatio: { min: 0.4, max: 2.5 },
      },
      refVideoConstraints: {
        formats: ['mp4', 'mov'],
        maxFileSizeMB: 50,
        duration: { min: 2, max: 15, totalMax: 15 },
        dimension: { min: 256, max: 5760 },
        aspectRatio: { min: 0.4, max: 2.5 },
      },
      refAudioConstraints: {
        formats: ['wav', 'mp3'],
        maxFileSizeMB: 15,
        duration: { min: 2, max: 15, totalMax: 15 },
      },
    },
  },
};
